/**
 * Copilot action registry — the bridge between noki-fe components and
 * the voice agent.
 *
 * Module-level singleton. The standalone widget uses postMessage between
 * an iframe and the host page; in noki-fe the copilot lives inside the
 * React tree, so we use a process-wide map instead. Any noki-fe
 * component that wants to expose an agent-callable action calls
 * `useCopilotAction(descriptor, handler)`; the registry hands the
 * descriptor to the NokiCopilot panel which forwards it over the
 * control plane to the server.
 *
 * Why a singleton, not a React Context: keeping it module-scoped means
 * mounting the copilot doesn't require any wrapping in App.jsx —
 * `<NokiCopilot />` and the action consumers just `import` from this
 * module. The trade-off is that it's not isolatable per test; that's
 * fine because there is only ever one copilot per app.
 *
 * Action lifecycle is mount/unmount per component — the hook wraps the
 * imperative `register` API in a `useEffect`, so a screen that owns an
 * action only exposes it while it's mounted. Stale handlers from
 * unmounted screens never get called.
 *
 * Per-region scoping (per voice_copilot_design.md §7):
 *   Each action declares a `scope`:
 *     - "global" (default) — always advertised, irrespective of which
 *       protocol is active. Used for the always-on top-level actions
 *       like `set_theme`, `view_notifications`, `navigate`.
 *     - <pageId> string — advertised ONLY when a protocol with that
 *       pageId is in the currently-active set (e.g. an action
 *       registered with scope="select_patient_drawer" only shows up
 *       when that drawer is open). Lets the same action name be
 *       reused across pages without colliding (the action registered
 *       under the active protocol wins).
 *
 *   Backward-compat: omitting `scope` defaults to "global", so every
 *   existing useCopilotAction caller keeps its current behaviour.
 */

import { useEffect, useRef } from "react";

/** Internal map: key -> { descriptor, handler, scope }.
 *
 *  Key is `${scope}::${name}` so the same action name can be registered
 *  under multiple scopes simultaneously (e.g. "filter" on the patient
 *  list page AND on the dashboard's recent-patients widget). The
 *  active-protocol filter at list time picks the right one.
 */
const actions = new Map();

/** Subscribers notified whenever the action set mutates. */
const subscribers = new Set();

let revision = 0;

const notify = () => {
  revision += 1;
  subscribers.forEach((cb) => {
    try {
      cb(revision);
    } catch (error) {
      // Never let a buggy subscriber break the broadcast for the rest.
      // eslint-disable-next-line no-console
      console.warn("[copilot] action subscriber threw:", error);
    }
  });
};

const SCOPE_GLOBAL = "global";

const makeKey = (scope, name) => `${scope}::${name}`;

/**
 * Structured-outcome convention.
 *
 * A handler has three ways to report its result, and the distinction is
 * semantic — the server (and the model) branch on `status` + `error_code`:
 *
 *   1. return a plain value            -> status:"ok"  (the success path)
 *   2. return copilotBlocked(code,...) -> status:"blocked" + that code
 *      / copilotError(code,...)        -> status:"error"   + that code
 *   3. throw                           -> status:"error", host_exception
 *
 * Throwing is reserved for GENUINE faults (a bug, an unwired handler) —
 * it always surfaces as the opaque `host_exception`. An EXPECTED
 * non-success outcome the model should reason about (a form that failed
 * its own validation, a domain precondition the FE detects) must instead
 * be RETURNED via these helpers so it arrives as a clean, typed result
 * with the right `error_code` — never masquerading as either a success
 * or a crash. One convention, applied by every handler that needs it
 * (pattern, not per-tool special-casing in the invoker).
 */
const COPILOT_OUTCOME = Symbol("copilotOutcome");

const makeOutcome = (status, errorCode, errorMessage, data) => ({
  [COPILOT_OUTCOME]: true,
  status,
  errorCode,
  errorMessage,
  data: data ?? null,
});

/** Expected, non-crash rejection the model can reason about + retry once
 *  state changes (e.g. `validation_failed`). Maps to a blocked result. */
export const copilotBlocked = (errorCode, errorMessage, data) =>
  makeOutcome("blocked", errorCode, errorMessage, data);

/** A genuine operation failure that isn't a code crash (e.g. an API call
 *  that returned an error). Maps to an error result with your code. */
export const copilotError = (errorCode, errorMessage, data) =>
  makeOutcome("error", errorCode, errorMessage, data);

const isCopilotOutcome = (value) =>
  typeof value === "object" && value !== null && value[COPILOT_OUTCOME] === true;

/**
 * Imperative registration. Returns a cleanup function. Pair with the
 * `useCopilotAction` hook from React components — only reach for this
 * directly if you're outside a hook (rare).
 *
 * `scope` defaults to "global" — backward-compatible with all existing
 * callers. Pass a pageId to scope the action to a specific protocol;
 * see `listCopilotActions(activeProtocolIds)` for filtering semantics.
 */
export function registerCopilotAction(descriptor, handler, opts) {
  if (!descriptor || typeof descriptor.name !== "string" || !descriptor.name) {
    return () => {};
  }
  if (typeof handler !== "function") {
    return () => {};
  }
  const scope =
    opts && typeof opts.scope === "string" && opts.scope.length > 0
      ? opts.scope
      : SCOPE_GLOBAL;
  const key = makeKey(scope, descriptor.name);
  const entry = { descriptor, handler, scope, name: descriptor.name };
  actions.set(key, entry);
  notify();
  return () => {
    const current = actions.get(key);
    // Only delete if WE are still the owner; a re-register with a
    // different handler shouldn't be wiped out by an earlier mount's
    // cleanup.
    if (current && current.handler === handler) {
      actions.delete(key);
      notify();
    }
  };
}

/**
 * List the descriptors of every currently-registered action whose
 * scope is active. If `activeProtocolIds` is omitted, returns ALL
 * actions (preserves the old shape for callers that haven't yet
 * adopted scoped filtering).
 *
 * Filtering rule:
 *   - scope === "global"  → always returned
 *   - scope === <pageId>  → returned only if pageId ∈ activeProtocolIds
 *
 * If two entries share the same name (one global, one scoped to an
 * active protocol), both are returned — that's a registration mistake
 * the orchestrator surfaces; the FE doesn't try to disambiguate.
 */
export function listCopilotActions(activeProtocolIds) {
  if (!activeProtocolIds) {
    return Array.from(actions.values()).map((entry) => entry.descriptor);
  }
  const activeSet =
    activeProtocolIds instanceof Set
      ? activeProtocolIds
      : new Set(activeProtocolIds);
  const out = [];
  for (const entry of actions.values()) {
    if (entry.scope === SCOPE_GLOBAL || activeSet.has(entry.scope)) {
      out.push(entry.descriptor);
    }
  }
  return out;
}

/**
 * Invoke an action by name. The lookup prefers a scoped entry over a
 * global one when `activeProtocolIds` is provided — drawer actions take
 * precedence over a same-named global action while their protocol is
 * active. With no `activeProtocolIds`, falls back to first-found order.
 */
export async function invokeCopilotAction(name, args, activeProtocolIds) {
  // eslint-disable-next-line no-console
  console.info("[copilot] action.invoke", { name, args });

  const entry = pickEntry(name, activeProtocolIds);
  if (!entry) {
    // eslint-disable-next-line no-console
    console.warn("[copilot] action.unknown", {
      name,
      registered: Array.from(actions.values()).map((e) => e.scope + "::" + e.name),
    });
    return {
      status: "error",
      errorCode: "unknown_action",
      errorMessage: `No handler registered for '${name}'`,
    };
  }
  try {
    const data = await entry.handler(args ?? {});
    // A handler may return a structured outcome (copilotBlocked/copilotError)
    // to report an EXPECTED non-success without throwing — forward it as a
    // typed result so the model sees the real error_code (e.g.
    // validation_failed) instead of the opaque host_exception a throw yields.
    if (isCopilotOutcome(data)) {
      // eslint-disable-next-line no-console
      console.info("[copilot] action.result", {
        name,
        scope: entry.scope,
        status: data.status,
        errorCode: data.errorCode,
      });
      return {
        status: data.status,
        errorCode: data.errorCode,
        errorMessage: data.errorMessage,
        data: data.data,
      };
    }
    // eslint-disable-next-line no-console
    console.info("[copilot] action.result", { name, scope: entry.scope, status: "ok" });
    return { status: "ok", data: data ?? null };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[copilot] action.exception", {
      name,
      scope: entry.scope,
      error: error?.message ?? String(error),
    });
    return {
      status: "error",
      errorCode: "host_exception",
      errorMessage: error?.message ?? "host handler threw",
    };
  }
}

const pickEntry = (name, activeProtocolIds) => {
  // First pass: scoped entries that match an active protocol win.
  if (activeProtocolIds) {
    const activeSet =
      activeProtocolIds instanceof Set
        ? activeProtocolIds
        : new Set(activeProtocolIds);
    for (const entry of actions.values()) {
      if (entry.name !== name) continue;
      if (entry.scope !== SCOPE_GLOBAL && activeSet.has(entry.scope)) {
        return entry;
      }
    }
  }
  // Second pass: global match.
  const globalEntry = actions.get(makeKey(SCOPE_GLOBAL, name));
  if (globalEntry) return globalEntry;
  // Last resort: any entry by name.
  for (const entry of actions.values()) {
    if (entry.name === name) return entry;
  }
  return null;
};

/** Subscribe to revision changes. Returns an unsubscribe fn. */
export function subscribeCopilotActions(callback) {
  if (typeof callback !== "function") return () => {};
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

export function getCopilotActionRevision() {
  return revision;
}

/**
 * Hook for noki-fe components.
 *
 *   useCopilotAction(
 *     {
 *       name: "navigate",
 *       description: "Navigate to a different page in noki-fe.",
 *       classification: "READ",
 *       parameters: [
 *         { name: "to", type: "string", description: "Route path", required: true },
 *       ],
 *     },
 *     async ({ to }) => {
 *       navigate(to);
 *       return { route: to };
 *     },
 *   );
 *
 * For region-scoped actions (drawer / modal), pass an `opts.scope`
 * string equal to the parent region's `pageId`:
 *
 *   useCopilotAction(descriptor, handler, { scope: "select_patient_drawer" });
 *
 * The action then advertises only while a protocol with that pageId
 * is in the currently-active set.
 *
 * The action registers on mount and unregisters on unmount. The handler
 * is captured behind a ref so re-renders don't churn the registration —
 * we only re-register when `descriptor` identity (or `scope`) changes.
 */
export function useCopilotAction(descriptor, handler, opts) {
  // Keep the latest handler in a ref so the registered closure always
  // calls the freshest function without re-registering on every render.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const scope = opts && typeof opts.scope === "string" ? opts.scope : undefined;

  useEffect(() => {
    if (!descriptor || !descriptor.name) return undefined;
    const stableHandler = (args) => handlerRef.current(args);
    return registerCopilotAction(descriptor, stableHandler, scope ? { scope } : undefined);
    // We re-register when descriptor identity changes OR scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor, scope]);

  // Return nothing — the registration's only side effect is the panel
  // discovering the new action via subscribeCopilotActions().
}
